import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import SharedHeader from "@/components/SharedHeader";
import { useAuth } from "@/contexts/AuthContext";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

function AccordionItem({ 
  question, 
  answer, 
  isOpen, 
  onClick 
}: { 
  question: string; 
  answer: string; 
  isOpen: boolean; 
  onClick: () => void;
}) {
  return (
    <div className={cn(
      "rounded-xl overflow-hidden",
      theme.borders.neubrutalism,
      "bg-white dark:bg-gray-800",
      theme.shadows.neubrutalismSm
    )}>
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <h3 className="text-lg font-bold text-gray-900 dark:text-white pr-4">
          {question}
        </h3>
        <ChevronDown 
          className={cn(
            "w-5 h-5 flex-shrink-0 transition-transform text-gray-900 dark:text-white",
            isOpen && "rotate-180"
          )} 
        />
      </button>
      
      {isOpen && (
        <div className="px-6 pb-6 pt-0">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/episodes');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "What is Chatpod GPT?",
          a: "Chatpod GPT lets you chat with any podcast. Ask questions, get answers with timestamps, and explore topics deeper—all powered by the actual podcast content, not generic internet results."
        },
        {
          q: "How do I get started?",
          a: "Paste any podcast's RSS feed and start chatting. Many popular podcasts are ready instantly—for others, we transcribe episodes in about 60 seconds. You're not limited to subscribed podcasts; explore any show with an RSS feed."
        },
        {
          q: "Do I need technical knowledge?",
          a: "Nope. If you can paste a URL and type a question, you're good. No coding, no downloads, no complicated setup."
        },
        {
          q: "Can I chat with podcasts I'm not subscribed to?",
          a: "Yes! You can chat with any podcast that has an RSS feed. Just paste the URL and start asking questions. Many popular podcasts are already in our system for instant access. Subscribing is optional—it's useful if you want ongoing access, new episode notifications, and to build up a library of your favorites."
        }
      ]
    },
    {
      category: "Pricing & Billing",
      questions: [
        {
          q: "Can I change or cancel my plan?",
          a: "Absolutely. You can upgrade, downgrade, or cancel your subscription at any time from your dashboard. Changes take effect immediately, and we'll prorate any billing differences. Please note: Canceling your subscription will permanently delete all data related to your account, including podcasts, episodes, transcripts, and chat history. This action cannot be undone."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards (Visa, MasterCard, American Express, Discover) and PayPal. All payments are processed securely through Stripe."
        },
        {
          q: "Do you offer refunds?",
          a: "No, we do not offer refunds. Since we charge for AI usage (chat requests and transcriptions) that cannot be refunded, all purchases are final."
        },
        {
          q: "How are usage charges calculated?",
          a: "Chat requests are billed at $0.01 per request. Transcription is billed at $0.10 per compute-second. As a rough estimate, a typical episode costs about $0.02 to transcribe. Usage charges apply after your plan's included credits are used up."
        }
      ]
    },
    {
      category: "Features & Integration",
      questions: [
        {
          q: "Which podcasts can I subscribe to?",
          a: "You can chat with any podcast that has an RSS feed—you don't need to subscribe to each one. Just paste the feed URL to start exploring. Subscribing gives you ongoing access and notifications, but you can try any show instantly. Works especially well with information-dense, educational, and in-depth podcasts."
        },
        {
          q: "How accurate are the AI responses?",
          a: "Our AI provides accurate responses based on podcast transcripts. It references specific timestamps and quotes from episodes, helping you find exactly what you're looking for. The AI understands context and can explain complex concepts, historical events, and detailed information."
        },
        {
          q: "Can I ask questions about old episodes?",
          a: "Yes! Once you subscribe to a podcast, you can ask questions about any episode in that podcast's catalog, whether it's the latest release or an episode from years ago."
        },
        {
          q: "Can I use Chatpod GPT with podcasts in other languages?",
          a: "Yes! Chatpod GPT features automatic translation. You can subscribe to podcasts in any language and ask questions in your native language. The AI automatically translates both the podcast content and your questions, so you can engage with podcasts from around the world seamlessly."
        },
        {
          q: "What languages does Chatpod GPT support?",
          a: "Chatpod GPT supports podcasts and questions in all major languages including English, Spanish, French, German, Portuguese, Italian, Chinese, Japanese, Korean, Arabic, and many more. You can ask questions in your preferred language regardless of the podcast's language."
        }
      ]
    },
    {
      category: "Privacy & Security",
      questions: [
        {
          q: "How is my data stored?",
          a: "All podcast transcripts and chat data are encrypted at rest and in transit using industry-standard encryption (AES-256). We store data in secure, SOC 2 compliant data centers."
        },
        {
          q: "Who owns my chat data?",
          a: "You do! You retain full ownership of your chat history and conversations. We never share or sell your data to third parties. Your subscription data and chat history are private to you."
        },
        {
          q: "Is Chatpod GPT GDPR compliant?",
          a: "Yes, Chatpod GPT is fully GDPR compliant. We provide tools for data export, deletion requests, and consent management to help you meet your privacy obligations."
        }
      ]
    },
    {
      category: "Support & Resources",
      questions: [
        {
          q: "What kind of support do you offer?",
          a: "All plans include email support. Basic plans get standard support, while Premium plans receive priority support. We're here to help you get the most out of your podcast listening experience."
        },
        {
          q: "Do you have documentation or tutorials?",
          a: "Yes! We provide comprehensive documentation, video tutorials, and step-by-step guides for all features. Our help center is available 24/7 and regularly updated with new resources."
        },
        {
          q: "Can I request new features?",
          a: "Absolutely! We love hearing from our users. You can submit feature requests through our feedback portal, and we regularly incorporate user suggestions into our product roadmap."
        }
      ]
    }
  ];

  let globalIndex = 0;

  return (
    <div className={cn("min-h-screen", theme.colors.backgroundCream)}>
      <SharedHeader />

      <main className="mx-auto max-w-4xl px-6 py-16 pt-24">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-black mb-6 text-gray-900 dark:text-white">
            Questions? Answered.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300">
            Everything you need to know before getting started
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-12">
          {faqs.map((section) => (
            <div key={section.category}>
              <h2 className="text-2xl font-black mb-6 text-gray-900 dark:text-white">
                {section.category}
              </h2>
              
              <div className="space-y-4">
                {section.questions.map((faq) => {
                  const currentIndex = globalIndex++;
                  return (
                    <AccordionItem
                      key={currentIndex}
                      question={faq.q}
                      answer={faq.a}
                      isOpen={openIndex === currentIndex}
                      onClick={() => setOpenIndex(openIndex === currentIndex ? null : currentIndex)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions? */}
        <div className={cn(
          "mt-16 p-12 rounded-2xl text-center",
          theme.cards.base
        )}>
          <h2 className="text-3xl font-black mb-4 text-gray-900 dark:text-white">
            Still have questions?
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 mb-6">
            Our team is here to help. Get in touch and we'll respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className={cn(theme.buttons.primary, "px-8 py-3 font-semibold")}
            >
              <a href="mailto:quiet.lens2873@fastmail.com">
                Email Support
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className={cn(theme.buttons.secondary, "px-8 py-3 font-semibold")}
            >
              <Link to="/pricing">
                View Pricing
              </Link>
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className={cn(
        "border-t-3 border-gray-900 dark:border-gray-300",
        theme.colors.cardCream,
        "mt-16"
      )}>
        <div className="mx-auto max-w-6xl px-6 py-8 text-center text-sm text-gray-600 dark:text-gray-400">
          <p>© {new Date().getFullYear()} Chatpod GPT. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

